import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProfile, updateProfile, uploadAvatar, uploadLogo, getMyServices, createService, updateService, getMyAttendance, getMyInvitados, getUpcomingEvents } from '../api/api';
import { SkeletonPage, Avatar, MexPhoneInput, EVENT_TYPE_LABELS, EVENT_MODALITY_LABELS, formatEventDate, formatEventTime, AddToCalendarButton, isValidClabe } from '../components/Charts';
import { uiForm } from '../components/Modal';
import { uiToastSuccess, uiToastError } from '../components/Toast';

const ALLOWED_AVATAR_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

const INVITADO_STATUS_LABELS = { pendiente: 'Pendiente', aprobado: 'Aprobado', rechazado: 'Rechazado' };

// Normaliza teléfonos guardados antes de que existiera el formato "+52XXXXXXXXXX"
// (por ejemplo "8124684325" a secas) a ese formato, para que no truene el guardado
// si el usuario nunca vuelve a tocar el campo.
function normalizeMxPhone(value) {
  const digits = String(value || '').replace(/\D/g, '').slice(-10);
  return digits.length === 10 ? `+52${digits}` : (value || '');
}

function MiPerfil({ onProfileSaved }) {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({ name: '', business_name: '', phone: '', avatar_url: '', business_logo_url: '', bank_name: '', clabe: '', account_holder: '' });
  const [services, setServices] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [myInvitados, setMyInvitados] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [newService, setNewService] = useState({ service_name: '', description: '', commission_percentage: '', commission_base: 'factura' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    getProfile()
      .then(res => {
        setProfile(res.data);
        setForm({
          name: res.data.name || '',
          business_name: res.data.business_name || '',
          phone: normalizeMxPhone(res.data.phone),
          avatar_url: res.data.avatar_url || '',
          business_logo_url: res.data.business_logo_url || '',
          bank_name: res.data.bank_name || '',
          clabe: res.data.clabe || '',
          account_holder: res.data.account_holder || '',
        });
        loadServices();
        if (res.data.status === 'active') {
          getMyAttendance().then(r => setAttendance(r.data)).catch(() => {});
          getMyInvitados().then(r => setMyInvitados(r.data)).catch(() => {});
          getUpcomingEvents().then(r => setUpcomingEvents(r.data)).catch(() => {});
        }
      })
      .catch(() => navigate('/'));
  }, [navigate]);

  const loadServices = () => {
    getMyServices().then(res => setServices(res.data)).catch(() => {});
  };

  const handleProfileChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleAvatarFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (!ALLOWED_AVATAR_TYPES.includes(file.type)) {
      setMsg({ type: 'error', text: 'Solo se aceptan imágenes JPG, PNG, WEBP o GIF' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setMsg({ type: 'error', text: 'La imagen no debe pesar más de 5 MB' });
      return;
    }
    setUploadingAvatar(true);
    setMsg(null);
    try {
      const res = await uploadAvatar(file);
      setForm(f => ({ ...f, avatar_url: res.data.avatar_url }));
      setProfile(p => ({ ...p, avatar_url: res.data.avatar_url }));
      setMsg({ type: 'success', text: 'Foto de perfil actualizada' });
      await refreshParentProfile();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Error al subir la foto' });
    }
    setUploadingAvatar(false);
  };

  const handleLogoFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (!ALLOWED_AVATAR_TYPES.includes(file.type)) {
      setMsg({ type: 'error', text: 'Solo se aceptan imágenes JPG, PNG, WEBP o GIF' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setMsg({ type: 'error', text: 'La imagen no debe pesar más de 5 MB' });
      return;
    }
    setUploadingLogo(true);
    setMsg(null);
    try {
      const res = await uploadLogo(file);
      setForm(f => ({ ...f, business_logo_url: res.data.business_logo_url }));
      setProfile(p => ({ ...p, business_logo_url: res.data.business_logo_url }));
      setMsg({ type: 'success', text: 'Logo del negocio actualizado' });
      await refreshParentProfile();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Error al subir el logo' });
    }
    setUploadingLogo(false);
  };

  const refreshParentProfile = async () => {
    if (!onProfileSaved) return;
    try {
      const res = await getProfile();
      setProfile(res.data);
      onProfileSaved(res.data);
    } catch (err) { /* no-op */ }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    // CLABE: si escribió algo, tiene que ser una CLABE completa y válida (18 dígitos +
    // dígito verificador) — no se manda a medias. El backend vuelve a checar esto de
    // todas formas; esto es solo para no hacerle el viaje redondo si ya se sabe que va a
    // fallar.
    if (form.clabe && !isValidClabe(form.clabe)) {
      setMsg({ type: 'error', text: 'La CLABE no es válida — revisa que sean 18 dígitos correctos.' });
      return;
    }
    setSaving(true);
    try {
      // Admin/owner: el negocio es opcional (a petición de Jesús Guevara, que también tiene
      // negocio en la red). El backend acepta business_name vacío para staff; el logo se
      // sube aparte, así que no se manda business_logo_url desde aquí.
      // Datos bancarios se mandan para todos: admin/owner también refieren y cobran comisión.
      const staffAtSaveTime = profile && ['admin', 'owner'].includes(profile.role);
      const payload = staffAtSaveTime
        ? { name: form.name, business_name: form.business_name, phone: form.phone, avatar_url: form.avatar_url, bank_name: form.bank_name, clabe: form.clabe, account_holder: form.account_holder }
        : form;
      await updateProfile(payload);
      setMsg({ type: 'success', text: 'Perfil actualizado' });
      await refreshParentProfile();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Error al guardar perfil' });
    }
    setSaving(false);
  };

  const handleServiceChange = (e) => setNewService({ ...newService, [e.target.name]: e.target.value });

  const addService = async (e) => {
    e.preventDefault();
    try {
      await createService(newService);
      setNewService({ service_name: '', description: '', commission_percentage: '', commission_base: 'factura' });
      loadServices();
      await refreshParentProfile();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Error al agregar servicio' });
    }
  };

  const toggleActive = async (svc) => {
    await updateService(svc.id, { active: !svc.active });
    loadServices();
  };

  const changeCommissionBase = async (svc, commission_base) => {
    await updateService(svc.id, { commission_base });
    loadServices();
  };

  // Editar nombre, descripción y % de comisión de un servicio ya dado de alta — antes solo
  // se podía activar/desactivar o cambiar la base, para cambiar lo demás había que crear uno
  // nuevo y dejar el viejo como basura desactivada.
  const editService = async (svc) => {
    const values = await uiForm('Editar servicio', [
      { name: 'service_name', label: 'Nombre del servicio', type: 'text', value: svc.service_name },
      { name: 'description', label: 'Descripción', type: 'text', value: svc.description || '' },
      { name: 'commission_percentage', label: '% de comisión (si lo cambias, aplica solo a referencias nuevas)', type: 'text', value: String(svc.commission_percentage) },
    ]);
    if (!values) return;
    const pct = parseFloat(values.commission_percentage);
    if (isNaN(pct) || pct < 0 || pct > 100) {
      await uiToastError('El % de comisión debe ser un número entre 0 y 100.');
      return;
    }
    try {
      await updateService(svc.id, {
        service_name: values.service_name,
        description: values.description || null,
        commission_percentage: pct,
      });
      uiToastSuccess('Servicio actualizado.');
      loadServices();
    } catch (err) {
      uiToastError(err.response?.data?.error || 'No se pudo actualizar el servicio');
    }
  };

  if (!profile) return <SkeletonPage />;

  // Admin/owner son staff del hub — no necesitan negocio ni servicio para desbloquear el
  // sitio (ver GET /api/profile en server.js), pero pueden registrarlos de forma opcional.
  const isStaff = ['admin', 'owner'].includes(profile.role);

  return (
    <div className="page-narrow profile-page">
      <h1 className="page-title">Mi Perfil</h1>
      <p className="page-subtitle">{isStaff ? 'Tus datos de contacto y, si tienes un negocio en la red, tus servicios.' : 'Tus datos de contacto y los servicios que ofreces a la red.'}</p>

      {profile.status !== 'active' && (
        <div className="msg-banner error">Tu cuenta está pendiente de aprobación. Puedes completar tus datos mientras tanto.</div>
      )}
      {!(profile.profile_complete && profile.has_services) && (
        <div className="msg-banner error">
          {isStaff
            ? 'Debes completar tu nombre y teléfono antes de usar el resto del sitio.'
            : 'Debes completar tus datos generales y registrar al menos un servicio antes de usar el resto del sitio.'}
        </div>
      )}
      {msg && <div className={`msg-banner ${msg.type}`}>{msg.text}</div>}

      <div className="profile-top-row">
      <div className="card">
        <h2 className="card-title">Datos generales</h2>
        <form onSubmit={saveProfile}>
          <div className="form-group">
            <label className="form-label">Correo electrónico</label>
            <input type="email" value={profile.email || ''} disabled className="form-input" style={{ background: 'var(--neutral-50)', color: 'var(--neutral-500)' }} />
          </div>
          <div className="form-group">
            <label className="form-label">Foto de perfil</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <Avatar src={form.avatar_url} seed={form.name || profile.name} size={64} />
              <div style={{ flex: 1 }}>
                <label className="btn btn-secondary btn-sm" style={{ display: 'inline-block', cursor: uploadingAvatar ? 'default' : 'pointer', opacity: uploadingAvatar ? 0.6 : 1 }}>
                  {uploadingAvatar ? 'Subiendo...' : 'Subir foto'}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handleAvatarFile}
                    disabled={uploadingAvatar}
                    style={{ display: 'none' }}
                  />
                </label>
                <span className="form-hint" style={{ display: 'block', marginTop: '6px' }}>JPG, PNG, WEBP o GIF. Máximo 5 MB.</span>
              </div>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Nombre completo</label>
            <input type="text" name="name" value={form.name} onChange={handleProfileChange} required className="form-input" />
          </div>
          <div className="form-group">
            <label className="form-label">Nombre del negocio{isStaff ? ' (opcional)' : ''}</label>
            <input type="text" name="business_name" value={form.business_name} onChange={handleProfileChange} required={!isStaff} className="form-input" />
            {isStaff && (
              <span className="form-hint">Si lo llenas y agregas servicios, tu negocio aparece en el catálogo para recibir referencias.</span>
            )}
          </div>
          <div className="form-group">
              <label className="form-label">Logo del negocio{isStaff ? ' (opcional)' : ''}</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                {form.business_logo_url
                  ? <img src={form.business_logo_url} alt="Logo" style={{ width: 64, height: 64, borderRadius: 8, objectFit: 'cover', border: '1px solid var(--border-subtle)' }} />
                  : <div style={{ width: 64, height: 64, borderRadius: 8, background: 'var(--neutral-50)', border: '1px solid var(--border-subtle)' }} />}
                <div style={{ flex: 1 }}>
                  <label className="btn btn-secondary btn-sm" style={{ display: 'inline-block', cursor: uploadingLogo ? 'default' : 'pointer', opacity: uploadingLogo ? 0.6 : 1 }}>
                    {uploadingLogo ? 'Subiendo...' : 'Subir logo'}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={handleLogoFile}
                      disabled={uploadingLogo}
                      style={{ display: 'none' }}
                    />
                  </label>
                  <span className="form-hint" style={{ display: 'block', marginTop: '6px' }}>JPG, PNG, WEBP o GIF. Máximo 5 MB.</span>
                </div>
              </div>
          </div>
          <div className="form-group">
            <label className="form-label">Teléfono</label>
            <MexPhoneInput value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} required />
          </div>

          {/* Datos bancarios — a petición de Jesús Guevara, para pagar comisiones por
              transferencia. Solo el propio miembro los captura (nadie más los escribe por
              él) y solo el owner puede verlos después, del lado de Admin. */}
          <h3 style={{ fontSize: '15px', fontWeight: 700, margin: '22px 0 4px' }}>Datos bancarios</h3>
          <p className="form-hint" style={{ marginBottom: '14px' }}>Para pagarte comisiones por transferencia. Solo tú y el dueño de la cuenta pueden verlos.</p>
          <div className="two-col-grid">
            <div className="form-group">
              <label className="form-label">Banco</label>
              <input type="text" name="bank_name" value={form.bank_name} onChange={handleProfileChange} className="form-input" placeholder="Ej. BBVA" />
            </div>
            <div className="form-group">
              <label className="form-label">Titular de la cuenta</label>
              <input type="text" name="account_holder" value={form.account_holder} onChange={handleProfileChange} className="form-input" placeholder="Nombre tal cual está en el banco" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">CLABE</label>
            <input
              type="text"
              name="clabe"
              value={form.clabe}
              onChange={(e) => setForm({ ...form, clabe: e.target.value.replace(/\D/g, '').slice(0, 18) })}
              className="form-input"
              inputMode="numeric"
              placeholder="18 dígitos"
            />
            {form.clabe && form.clabe.length === 18 && !isValidClabe(form.clabe) && (
              <span className="form-hint" style={{ color: 'var(--danger-text)' }}>Esta CLABE no es válida — revisa que no te hayas equivocado de dígito.</span>
            )}
          </div>

          <button type="submit" disabled={saving} className="btn btn-primary">{saving ? 'Guardando...' : 'Guardar datos'}</button>
        </form>
      </div>

      <div className="card">
        <h2 className="card-title">Mis Servicios{isStaff ? ' (opcional)' : ''}</h2>
        <div className="table-wrap" style={{ marginBottom: '20px', maxHeight: '260px', overflowY: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Servicio</th>
                <th>% Comisión</th>
                <th>Base</th>
                <th>Activo</th>
                <th>Acción</th>
              </tr>
            </thead>
            <tbody>
              {services.map(s => (
                <tr key={s.id}>
                  <td>{s.service_name}</td>
                  <td>{s.commission_percentage}%</td>
                  <td>
                    <select
                      className="form-select"
                      value={s.commission_base || 'factura'}
                      onChange={(e) => changeCommissionBase(s, e.target.value)}
                    >
                      <option value="factura">Factura</option>
                      <option value="utilidad">Utilidad</option>
                    </select>
                  </td>
                  <td>
                    <button onClick={() => toggleActive(s)} className="btn btn-secondary btn-sm">{s.active ? 'Desactivar' : 'Activar'}</button>
                  </td>
                  <td>
                    <button onClick={() => editService(s)} className="btn btn-secondary btn-sm">Editar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {services.length === 0 && <div className="table-empty">Aún no tienes servicios registrados</div>}
        </div>

        <h3 style={{ fontSize: '15px', fontWeight: 700, marginBottom: '14px' }}>Agregar servicio</h3>
        <form onSubmit={addService}>
          <div className="form-group">
            <label className="form-label">Nombre del servicio</label>
            <input type="text" name="service_name" value={newService.service_name} onChange={handleServiceChange} required className="form-input" />
          </div>
          <div className="form-group">
            <label className="form-label">Descripción</label>
            <input type="text" name="description" value={newService.description} onChange={handleServiceChange} className="form-input" />
          </div>
          <div className="form-group">
            <label className="form-label">% Comisión que ofreces sobre el trato cerrado</label>
            <input type="number" step="0.01" name="commission_percentage" value={newService.commission_percentage} onChange={handleServiceChange} required className="form-input" />
            <span className="form-hint">El hub se queda con un 30% fijo de esta comisión; el 70% restante es para quien te refirió al cliente. Cada referencia conserva el % vigente el día que se creó.</span>
          </div>
          <div className="form-group">
            <label className="form-label">Base de la comisión</label>
            <select name="commission_base" value={newService.commission_base} onChange={handleServiceChange} className="form-select">
              <option value="factura">Factura (monto total del trato)</option>
              <option value="utilidad">Utilidad (ganancia neta del trato)</option>
            </select>
            <span className="form-hint">Define sobre qué monto se calcula tu comisión al reportar un negocio cerrado.</span>
          </div>
          <button type="submit" className="btn btn-primary">Agregar servicio</button>
        </form>
      </div>
      </div>

      {profile.status === 'active' && upcomingEvents.length > 0 && (
        <div className="card">
          <h2 className="card-title">Próximos eventos</h2>
          {upcomingEvents.map(ev => (
            <div key={ev.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--border-subtle)' }}>
              <div style={{ fontWeight: 700, fontSize: '14px' }}>
                {EVENT_TYPE_LABELS[ev.event_type] || ev.event_type} — {EVENT_MODALITY_LABELS[ev.modality] || ev.modality}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--neutral-500)', textTransform: 'capitalize' }}>
                {formatEventDate(ev.event_date)}{ev.event_time ? ` · ${formatEventTime(ev.event_time)}` : ''}
              </div>
              <AddToCalendarButton event={ev} />
            </div>
          ))}
        </div>
      )}

      {profile.status === 'active' && (
        <div className="card">
          <h2 className="card-title">Mi asistencia a eventos</h2>
          <div className="table-wrap" style={{ marginBottom: '20px' }}>
            <table className="data-table">
              <thead>
                <tr><th>Evento</th><th>Modalidad</th><th>Fecha</th></tr>
              </thead>
              <tbody>
                {attendance.map(a => (
                  <tr key={a.id}>
                    <td>{EVENT_TYPE_LABELS[a.event_type] || a.event_type}</td>
                    <td>{EVENT_MODALITY_LABELS[a.event_modality] || a.event_modality}</td>
                    <td style={{ textTransform: 'capitalize' }}>{formatEventDate(a.event_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {attendance.length === 0 && <div className="table-empty">Aún no te has registrado a ningún evento</div>}
          </div>

          <h3 style={{ fontSize: '15px', fontWeight: 700, marginBottom: '14px' }}>Invitados que has llevado</h3>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr><th>Nombre</th><th>Negocio</th><th>Estado</th></tr>
              </thead>
              <tbody>
                {myInvitados.map(i => (
                  <tr key={i.id}>
                    <td>{i.name}</td>
                    <td>{i.business_name || '—'}</td>
                    <td>{INVITADO_STATUS_LABELS[i.status] || i.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {myInvitados.length === 0 && <div className="table-empty">Aún no has registrado invitados</div>}
          </div>
        </div>
      )}
    </div>
  );
}

export default MiPerfil;
